import { useRef, useState, type ReactNode } from "react";
import * as Popover from "@radix-ui/react-popover";
import { keyframes, styled } from "@pigment-css/react";
import {
  BookOpenOutline,
  ChatBubbleOutline,
  CheckmarkThin,
  ChevronRightSmallOutline,
  GlobeOutline,
  LogoutOutline,
  SettingsGearOutline,
} from "@acto/web-common/components";
import { useTranslations, useLocale } from "@acto/web-common/locales";
import { toVirtualRef } from "@acto/taskpane/field-sales-2/utils/toVirtualRef";
import { InitialsAvatar } from "@acto/taskpane/field-sales-2/components/primitives/InitialsAvatar";
import { Divider } from "@acto/taskpane/field-sales-2/components/primitives/Divider";

const ANCHOR_GAP_PX = 12;

// Glass dropdown built like FilterPopover (the sibling popover in this same TopBar): portaled so it
// escapes the TopBar's `overflow: hidden` clip, and using the shared glass surface tokens for a
// consistent look. Opens directly under the avatar, aligned to its trailing (right) edge.
const popoverIn = keyframes({
  from: { opacity: 0, transform: "translateY(-8px) scale(0.96)" },
  to: { opacity: 1, transform: "translateY(0) scale(1)" },
});

const popoverOut = keyframes({
  from: { opacity: 1, transform: "scale(1)" },
  to: { opacity: 0, transform: "scale(0.96)" },
});

const PopoverContent = styled(Popover.Content)(({ theme }) => ({
  zIndex: 1000,
  minWidth: "15rem",
  maxWidth: "18rem",
  padding: `${theme.fieldSalesTwo.spacing[10]} ${theme.fieldSalesTwo.spacing[8]} ${theme.fieldSalesTwo.spacing[8]}`,
  borderRadius: theme.fieldSalesTwo.radius[32],
  background: theme.fieldSalesTwo.colors.surface.glassStrong,
  border: `1px solid ${theme.fieldSalesTwo.colors.surface.glassBorderStrong}`,
  backdropFilter: `blur(${theme.fieldSalesTwo.effects.blurSm})`,
  WebkitBackdropFilter: `blur(${theme.fieldSalesTwo.effects.blurSm})`,
  boxShadow: theme.fieldSalesTwo.shadows.glass,
  display: "flex",
  flexDirection: "column",
  transformOrigin: "top right",
  outline: "none",
  '&[data-align="start"]': {
    transformOrigin: "top left",
  },
  '&[data-state="open"]': {
    animation: `${popoverIn} ${theme.fieldSalesTwo.motion.duration.base} ${theme.fieldSalesTwo.motion.easing.emphasized}`,
  },
  '&[data-state="closed"]': {
    animation: `${popoverOut} ${theme.fieldSalesTwo.motion.duration.fast} ${theme.fieldSalesTwo.motion.easing.standard}`,
    pointerEvents: "none",
  },
  "@media (prefers-reduced-motion: reduce)": {
    animation: "none",
  },
}));

const Header = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[2],
  padding: `${theme.fieldSalesTwo.spacing[8]} ${theme.fieldSalesTwo.spacing[8]} ${theme.fieldSalesTwo.spacing[10]}`,
  minWidth: 0,
}));

const HeaderName = styled("span")(({ theme }) => ({
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[15],
  fontWeight: theme.fieldSalesTwo.font.weight[540],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  letterSpacing: theme.fieldSalesTwo.font.letterSpacing.tight,
  color: theme.fieldSalesTwo.colors.neutral[900],
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

const HeaderEmail = styled("span")(({ theme }) => ({
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[14],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  color: theme.fieldSalesTwo.colors.neutral[700],
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

// Shared fs2 Divider primitive (an <hr>, so it carries role="separator" inside the menu) with the
// menu's vertical breathing room added.
const SectionDivider = styled(Divider)(({ theme }) => ({
  marginBlock: theme.fieldSalesTwo.spacing[8],
}));

const Item = styled("button")(({ theme }) => ({
  width: "100%",
  height: theme.fieldSalesTwo.spacing[44],
  display: "flex",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[8],
  padding: `0 ${theme.fieldSalesTwo.spacing[8]}`,
  margin: 0,
  border: "none",
  background: "transparent",
  borderRadius: theme.fieldSalesTwo.radius[16],
  color: theme.fieldSalesTwo.colors.neutral[900],
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[15],
  fontWeight: theme.fieldSalesTwo.font.weight[540],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  letterSpacing: theme.fieldSalesTwo.font.letterSpacing.tight,
  cursor: "pointer",
  textAlign: "left",
  transition: "background-color 120ms ease",
  "&:hover": {
    backgroundColor: theme.fieldSalesTwo.colors.neutral[200],
  },
  "&:focus-visible": {
    outline: `${theme.strokeWidth.regular} solid ${theme.fieldSalesTwo.colors.neutral[700]}`,
    outlineOffset: `-${theme.strokeWidth.regular}`,
  },
}));

const IconSlot = styled("span")(({ theme }) => ({
  width: theme.fieldSalesTwo.spacing[24],
  height: theme.fieldSalesTwo.spacing[24],
  flex: "0 0 auto",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  color: theme.fieldSalesTwo.colors.neutral[850],
}));

const Label = styled("span")({
  flex: "1 1 0",
  minWidth: 0,
  textAlign: "left",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

// Trailing affordance for a menu row: the chevron on the language submenu trigger and the
// checkmark on the active-locale radio item. Sits after the flex-growing Label, so it hugs
// the right edge.
const TrailingSlot = styled("span")(({ theme }) => ({
  flex: "0 0 auto",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  color: theme.fieldSalesTwo.colors.neutral[800],
}));

// The language submenu reuses the parent's glass surface (PopoverContent) but is narrower and
// evenly padded — it only holds two short locale rows, so the header-biased padding isn't needed.
const SubMenuContent = styled(PopoverContent)(({ theme }) => ({
  minWidth: "11rem",
  maxWidth: "14rem",
  padding: theme.fieldSalesTwo.spacing[8],
}));

interface MenuItemDef {
  key: string;
  icon: ReactNode;
  label: string;
  onSelect: () => void;
}

export interface ProfileMenuProps {
  userName: string;
  userEmail: string;
  onLogout: () => void;
  /** Optional — when omitted (e.g. the `contact-support` flag is off) the item is hidden. */
  onContactSupport?: () => void;
  onOpenHelpCenter: () => void;
  /** When omitted, the item is hidden. */
  onOpenProfile?: () => void;
}

export function ProfileMenu({
  userName,
  userEmail,
  onLogout,
  onContactSupport,
  onOpenHelpCenter,
  onOpenProfile,
}: ProfileMenuProps) {
  const t = useTranslations("fieldSalesTwo.profile");
  const common = useTranslations("common");
  const [savingLocale, setSavingLocale] = useState(false);
  const [localeError, setLocaleError] = useState(false);
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  // When the menu closes because of an interaction *outside* it (e.g. clicking the filter
  // trigger in the same TopBar), focus has already moved to wherever the user clicked. Restoring
  // it to the avatar would yank focus out of that target — and if the target opened a sibling
  // popover, that popover sees a focus-outside and dismisses itself. Track the cause so
  // onCloseAutoFocus only restores focus for keyboard/select closes, not outside interactions.
  const closedByOutsideInteraction = useRef(false);

  const { locale, setLocale } = useLocale();
  const [languageOpen, setLanguageOpen] = useState(false);
  // The language submenu is a portaled child Popover, so a click/focus inside it reads as an
  // interaction *outside* the parent menu — track its content to keep the parent open (see
  // onInteractOutside below).
  const submenuContentRef = useRef<HTMLDivElement>(null);

  const supportItem: MenuItemDef[] = onContactSupport
    ? [{ key: "contactSupport", icon: <ChatBubbleOutline />, label: t("contactSupport"), onSelect: onContactSupport }]
    : [];

  // Links above (support/help), the language submenu in the middle, then the account (logout)
  // action below — each separated by a divider, mirroring the field-sales user menu's grouping.
  const linkItems: MenuItemDef[] = [
    ...supportItem,
    { key: "helpCenter", icon: <BookOpenOutline />, label: t("helpCenter"), onSelect: onOpenHelpCenter },
  ];
  const settingsItems: MenuItemDef[] = onOpenProfile
    ? [{ key: "myProfile", icon: <SettingsGearOutline />, label: t("myProfile"), onSelect: onOpenProfile }]
    : [];
  const accountItems: MenuItemDef[] = [
    { key: "logout", icon: <LogoutOutline />, label: t("logout"), onSelect: onLogout },
  ];

  const languages = [
    { locale: "de-DE", label: t("german") },
    { locale: "en-US", label: t("english") },
  ];

  const handleSelect = (item: MenuItemDef) => {
    setOpen(false);
    item.onSelect();
  };

  const handleSelectLocale = async (next: string) => {
    setSavingLocale(true);
    setLocaleError(false);
    try {
      await setLocale(next);
      setLanguageOpen(false);
      setOpen(false);
    } catch {
      setLocaleError(true);
    } finally {
      setSavingLocale(false);
    }
  };

  const renderItem = (item: MenuItemDef) => (
    <Item key={item.key} type="button" role="menuitem" onClick={() => handleSelect(item)}>
      <IconSlot aria-hidden="true">{item.icon}</IconSlot>
      <Label>{item.label}</Label>
    </Item>
  );

  return (
    <Popover.Root open={open} onOpenChange={setOpen} modal={false}>
      <InitialsAvatar
        ref={triggerRef}
        name={userName}
        onClick={() => setOpen((isOpen) => !isOpen)}
        ariaHasPopup="menu"
        ariaExpanded={open}
      />
      <Popover.Anchor virtualRef={toVirtualRef(triggerRef)} />
      <Popover.Portal>
        <PopoverContent
          side="bottom"
          align="end"
          sideOffset={ANCHOR_GAP_PX}
          role="menu"
          aria-orientation="vertical"
          aria-label={t("menuAriaLabel")}
          updatePositionStrategy="always"
          onInteractOutside={(e) => {
            // Let the avatar's own onClick toggle the menu. Without this, Radix closes on
            // pointerdown and the trigger's click then re-opens it.
            const target = e.target as Node | null;
            if (target && triggerRef.current?.contains(target)) {
              e.preventDefault();
              return;
            }
            // The language submenu is portaled outside this content, so interacting with it looks
            // like an outside interaction — keep the parent menu open while the submenu is used.
            if (target && submenuContentRef.current?.contains(target)) {
              e.preventDefault();
              return;
            }
            // A genuine outside interaction will close the menu and move focus elsewhere.
            closedByOutsideInteraction.current = true;
          }}
          onOpenAutoFocus={(e) => {
            // Radix focuses Content by default; let the first item take focus instead.
            e.preventDefault();
            const first = (e.currentTarget as HTMLElement | null)?.querySelector<HTMLButtonElement>(
              '[role="menuitem"]',
            );
            first?.focus();
          }}
          onCloseAutoFocus={(e) => {
            // No Popover.Trigger (we anchor via virtualRef), so Radix has nothing to restore focus
            // to on close — return it to the avatar ourselves, matching the v1 user menu.
            e.preventDefault();
            if (closedByOutsideInteraction.current) {
              // Focus already moved to the clicked target; don't steal it back.
              closedByOutsideInteraction.current = false;
              return;
            }
            triggerRef.current?.focus();
          }}
        >
          <Header>
            <HeaderName>{userName}</HeaderName>
            <HeaderEmail>{userEmail}</HeaderEmail>
          </Header>
          <SectionDivider />
          {linkItems.map(renderItem)}
          <SectionDivider />
          <Popover.Root open={languageOpen} onOpenChange={setLanguageOpen} modal={false}>
            <Popover.Trigger asChild>
              <Item type="button" role="menuitem" aria-haspopup="menu" aria-expanded={languageOpen}>
                <IconSlot aria-hidden="true">
                  <GlobeOutline />
                </IconSlot>
                <Label>{t("changeLanguage")}</Label>
                <TrailingSlot aria-hidden="true">
                  <ChevronRightSmallOutline />
                </TrailingSlot>
              </Item>
            </Popover.Trigger>
            <Popover.Portal>
              <SubMenuContent
                ref={submenuContentRef}
                side="bottom"
                align="end"
                sideOffset={ANCHOR_GAP_PX}
                role="menu"
                aria-orientation="vertical"
                aria-label={t("languageMenuAriaLabel")}
                updatePositionStrategy="always"
                onOpenAutoFocus={(e) => {
                  // Focus the active locale (or the first item) instead of the content container.
                  e.preventDefault();
                  const content = e.currentTarget as HTMLElement | null;
                  const active = content?.querySelector<HTMLButtonElement>(
                    '[role="menuitemradio"][aria-checked="true"]',
                  );
                  const first = content?.querySelector<HTMLButtonElement>('[role="menuitemradio"]');
                  (active ?? first)?.focus();
                }}
              >
                {languages.map((language) => (
                  <Item
                    key={language.locale}
                    type="button"
                    role="menuitemradio"
                    disabled={savingLocale}
                    aria-checked={locale === language.locale}
                    onClick={() => handleSelectLocale(language.locale)}
                  >
                    <Label>{language.label}</Label>
                    {locale === language.locale && (
                      <TrailingSlot aria-hidden="true">
                        <CheckmarkThin width={16} height={16} />
                      </TrailingSlot>
                    )}
                  </Item>
                ))}
                {localeError && <p role="alert">{common("languageSaveFailed")}</p>}
              </SubMenuContent>
            </Popover.Portal>
          </Popover.Root>
          {settingsItems.map(renderItem)}
          <SectionDivider />
          {accountItems.map(renderItem)}
        </PopoverContent>
      </Popover.Portal>
    </Popover.Root>
  );
}
